import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import * as FaIcons from 'react-icons/fa';
import * as LuIcons from 'react-icons/lu';
import { publicApi } from '../../services/publicApi';

const fallbackFooter = {
  description:
    'We build premium, modern, and responsive digital solutions tailored to elevate your business in the digital landscape.',
  copyrightText: 'RAADSO TECH. All rights reserved.',
  logoUrl: '/img/logo-black.png',
  privacyUrl: '#',
  termsUrl: '#',
  socialLinks: [
    { label: 'Twitter', url: '#', iconType: 'icon', icon: 'FaTwitter' },
    { label: 'LinkedIn', url: '#', iconType: 'icon', icon: 'FaLinkedin' },
    { label: 'GitHub', url: '#', iconType: 'icon', icon: 'FaGithub' },
    { label: 'Instagram', url: '#', iconType: 'icon', icon: 'FaInstagram' },
  ],
  quickLinks: [
    { label: 'Home', url: '#home' },
    { label: 'Services', url: '#services' },
    { label: 'Portfolio', url: '#portfolio' },
    { label: 'Contact', url: '#contact' },
  ],
};

function resolveIconComponent(iconName) {
  if (!iconName) return null;
  return FaIcons[iconName] || LuIcons[iconName] || null;
}

function mapLegacySocialLinks(footer) {
  return [
    { label: 'Twitter', url: footer.twitterUrl || '#', iconType: 'icon', icon: 'FaTwitter' },
    { label: 'LinkedIn', url: footer.linkedInUrl || '#', iconType: 'icon', icon: 'FaLinkedin' },
    { label: 'GitHub', url: footer.githubUrl || '#', iconType: 'icon', icon: 'FaGithub' },
    { label: 'Instagram', url: footer.instagramUrl || '#', iconType: 'icon', icon: 'FaInstagram' },
  ].filter((item) => item.url && item.url !== '#');
}

function FooterSocialButton({ link }) {
  if (!link?.url) return null;

  const IconComponent = link.iconType === 'icon' ? resolveIconComponent(link.icon) : null;

  return (
    <motion.a
      href={link.url}
      aria-label={link.label || 'Social link'}
      target="_blank"
      rel="noopener noreferrer"
      whileHover={{ scale: 1.1, y: -2 }}
      whileTap={{ scale: 0.95 }}
      className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-gray-100 text-gray-600 transition-colors duration-300 hover:bg-black hover:text-white dark:bg-black dark:text-white dark:hover:bg-black dark:hover:text-primary"
    >
      {link.iconType === 'image' && link.icon ? (
        <img src={link.icon} alt={link.label || 'Social'} className="h-5 w-5 object-contain" />
      ) : IconComponent ? (
        <IconComponent size={18} />
      ) : (
        <span className="text-xs font-bold">{(link.label || '?').slice(0, 1)}</span>
      )}
    </motion.a>
  );
}

const Footer = () => {
  const [footer, setFooter] = useState(fallbackFooter);
  const [contactInfo, setContactInfo] = useState({
    email: 'info@raadsotech.com',
    phone: '+252 61 1178748',
    address: 'howlwadag/ wahen',
  });

  useEffect(() => {
    let isMounted = true;
    const loadFooter = async () => {
      try {
        const [footerData, contactData] = await Promise.all([
          publicApi.getFooterContent(),
          publicApi.getContactInformation(),
        ]);

        if (isMounted && Array.isArray(footerData) && footerData.length > 0) {
          const selected = footerData[0];
          const socialLinks = Array.isArray(selected.socialLinks) && selected.socialLinks.length > 0
            ? selected.socialLinks.filter((item) => item?.url)
            : mapLegacySocialLinks(selected);
          const quickLinks = Array.isArray(selected.quickLinks) && selected.quickLinks.length > 0
            ? selected.quickLinks.filter((item) => item?.label || item?.url)
            : fallbackFooter.quickLinks;

          setFooter({
            description: selected.description || fallbackFooter.description,
            copyrightText: selected.copyrightText || fallbackFooter.copyrightText,
            logoUrl: selected.logoUrl || fallbackFooter.logoUrl,
            privacyUrl: selected.privacyUrl || '#',
            termsUrl: selected.termsUrl || '#',
            socialLinks,
            quickLinks,
          });
        }

        if (isMounted && Array.isArray(contactData) && contactData.length > 0) {
          const contact = contactData[0];
          setContactInfo({
            email: contact.email || contactInfo.email,
            phone: contact.phone || contactInfo.phone,
            address: contact.address || contactInfo.address,
          });
        }
      } catch {
        // Keep static fallback.
      }
    };

    loadFooter();
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <footer className="relative bg-primary text-black pt-24 pb-10 px-6 md:px-12 lg:px-24 overflow-hidden">
      <div className="absolute top-[-20%] right-[-10%] text-[40rem] font-black text-black/[0.05] select-none pointer-events-none leading-none rotate-12">
        R
      </div>

      <div className="relative z-10 max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
        <div className="col-span-1 md:col-span-2">
          <img
            src={footer.logoUrl}
            alt="RAADSO TECH Logo"
            className="h-12 md:h-16 object-contain mb-6"
          />
          <p className="text-gray-800 max-w-sm mb-8 leading-relaxed">
            {footer.description}
          </p>
          <div className="flex flex-wrap gap-4">
            {footer.socialLinks.map((link, index) => (
              <FooterSocialButton key={`${link.label}-${index}`} link={link} />
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-lg font-bold mb-6 text-black tracking-wider uppercase text-sm">Quick Links</h4>
          <ul className="flex flex-col gap-4 text-gray-800">
            {footer.quickLinks.map((link, index) => (
              <li key={`${link.label}-${index}`}>
                <a href={link.url || '#'} className="hover:text-white transition-colors">
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-lg font-bold mb-6 text-black tracking-wider uppercase text-sm">Contact</h4>
          <ul className="flex flex-col gap-4 text-gray-800">
            <li>{contactInfo.email}</li>
            <li>{contactInfo.phone}</li>
            <li>{contactInfo.address}</li>
          </ul>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto border-t border-black/15 pt-8 flex flex-col md:flex-row items-center justify-between text-gray-700 text-sm">
        <p>&copy; {new Date().getFullYear()} {footer.copyrightText}</p>
        <div className="flex gap-6 mt-4 md:mt-0">
          <a href={footer.privacyUrl} className="hover:text-white transition-colors">Privacy Policy</a>
          <a href={footer.termsUrl} className="hover:text-white transition-colors">Terms of Service</a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
